import { Node, mergeAttributes } from '@tiptap/core';
import { Bold } from '@tiptap/extension-bold';
import { Document } from '@tiptap/extension-document';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Underline } from '@tiptap/extension-underline';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import {
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor
} from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  CodeExtensionWithKeyboardShortcuts,
  RichTextEditorMenu
} from 'components/common/richTextEditor';

import type { ReactNodeViewProps } from '@tiptap/react';
import type { EditorStateProps } from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<EditorStateProps, 'editorLanguageActive'>;

// extend the editor events interface to include the answer active id event
declare module '@tiptap/core' {
  interface EditorEvents {
    'update:activeId': { activeId: string | null };
  }
}

const Answer = Node.create({
  atom: true,
  group: 'inline',
  inline: true,
  name: 'answer',
  selectable: false,
  addAttributes() {
    return {
      id: {
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) =>
          attributes.id ? { 'data-id': attributes.id } : {}
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      Backspace: () => {
        if (!this.options.getIsTranslationMode()) return false;

        const { state } = this.editor;
        const { selection, doc } = state;
        const { nodeBefore } = doc.resolve(selection.from);
        if (nodeBefore?.type.name === 'answer') return true;
        return false;
      },
      Delete: () => {
        if (!this.options.getIsTranslationMode()) return false;

        const { state } = this.editor;
        const { selection, doc } = state;
        const { nodeAfter } = doc.resolve(selection.to);
        if (nodeAfter?.type.name === 'answer') return true;
        return false;
      }
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer((props) => (
      <AnswerView
        {...props}
        isTranslating={this.options.getIsTranslationMode()}
      />
    ));
  },
  addOptions() {
    return {
      HTMLAttributes: { class: 'vuo-fillin-editor-answer' },
      getIsTranslationMode: () => false
    };
  },
  addStorage() {
    return { activeId: null };
  },
  parseHTML() {
    return [{ tag: 'answer[data-id]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'answer',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)
    ];
  }
});

const AnswerView = (props: ReactNodeViewProps & { isTranslating: boolean }) => {
  const { messages } = useReduxState();

  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    props.editor.on('update:activeId', ({ activeId }) => {
      setIsActive(activeId === props.node.attrs.id);
    });
    return () => {
      props.editor.off('update:activeId', () => setIsActive(false));
    };
  }, [props.editor, props.node.attrs.id]);

  const handleRemove: React.MouseEventHandler<SVGSVGElement> = useCallback(
    (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      props.deleteNode();
    },
    [props]
  );

  return (
    <NodeViewWrapper
      data-id={props.node.attrs.id}
      className={
        `vuo-fillin-editor-answer` +
        (isActive ? ' vuo-fillin-editor-answer-active' : '')
      }
    >
      <div className="vuo-fillin-editor-answer-remove">
        {props.isTranslating ? (
          <></>
        ) : (
          <Icon
            aria-label={messages.commonRemove}
            data-tooltip={messages.commonRemove}
            icon="times-circle"
            onClick={handleRemove}
            style={{ marginLeft: 40, marginBottom: -1 }}
          />
        )}
      </div>
    </NodeViewWrapper>
  );
};

interface Props {
  activeId: string;
  content: string;
  onChange: (content: string) => void;
  translationReferenceContent?: string;
}

const FillinEditor = ({
  activeId,
  content,
  onChange,
  translationReferenceContent,
  ...stateProps
}: Props & StateProps) => {
  const { editorLanguageActive } = stateProps;

  const { messages, theme } = useReduxState();

  const elementRef = useRef<HTMLDivElement>(null);
  const isTranslationRef = useRef(Boolean(editorLanguageActive));
  const lastEmittedRef = useRef<string | null>(null);
  const updateValueTimeout = useRef<NodeJS.Timeout>(undefined);

  const editor = useEditor({
    editorProps: { attributes: { class: 'vuo-fillin-editor' } },
    extensions: [
      Answer.configure({
        getIsTranslationMode: () => isTranslationRef.current
      }),
      Bold,
      Document,
      CodeExtensionWithKeyboardShortcuts,
      Italic,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: null, target: null }
      }),
      Paragraph,
      Placeholder.configure({
        placeholder: messages.blocksFillInContentPlaceholder,
        showOnlyWhenEditable: false
      }),
      Text,
      Underline,
      UndoRedo
    ],
    content,
    immediatelyRender: false,
    onUpdate({ editor }) {
      const editorValue = editor.getHTML();
      if (content === editorValue) return;

      clearTimeout(updateValueTimeout.current);
      updateValueTimeout.current = setTimeout(() => {
        lastEmittedRef.current = editorValue;
        onChange(editorValue);
      }, 500);
    }
  });

  useEffect(() => {
    return () => {
      clearTimeout(updateValueTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (!editor) return;
    editor.emit('update:activeId', { activeId });
  }, [activeId, editor]);

  useEffect(() => {
    if (!editor) return;

    // if this is triggered by the update we just sent, avoid
    if (content === lastEmittedRef.current) {
      lastEmittedRef.current = null;
      return;
    }

    if (content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
      lastEmittedRef.current = null;
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;

    // if we are translating a block and content is empty,
    // insert the placeholders from the translation reference
    const contentEmpty = !content || content === '<p></p>';
    if (isTranslationRef.current && contentEmpty) {
      const referenceAnswerIds = Array.from(
        translationReferenceContent?.matchAll(
          /<answer[^>]*data-id="(\d+)"[^>]*><\/answer>/g
        )
      ).map((match) => match[1] || '');
      if (referenceAnswerIds.length > 0) {
        const placeholderContent = referenceAnswerIds
          .map((id) => {
            return `<answer class="vuo-fillin-editor-answer" data-id="${id}"></answer>`;
          })
          .join('');

        editor.commands.setContent(`<p>${placeholderContent}</p>`, {
          emitUpdate: false
        });
      }
    }
  }, [editor, translationReferenceContent, content]);

  const insertAnswer = useCallback(() => {
    editor.commands.insertContent({
      type: 'answer',
      attrs: { id: Date.now().toString() }
    });
  }, [editor]);

  return (
    <div className="fillin-editor" ref={elementRef}>
      <EditorContent editor={editor} />

      {!isTranslationRef.current ? (
        <Button
          category="light"
          disabled={!editor}
          onClick={insertAnswer}
          text={messages.commonAddAnswer}
          style={{ marginTop: 10 }}
        />
      ) : null}

      {editor ? (
        <RichTextEditorMenu editor={editor} richTextElementRef={elementRef} />
      ) : null}

      <style jsx global>{`
        .vuo-fillin-editor {
          background-color: white;
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColor};
          counter-reset: fillin-answers;
          font-family: inherit;
          font-size: 1.6rem;
          font-weight: 400;
          line-height: 1.6;
          outline: none;
          overflow-wrap: break-word;
          padding: 10px 15px;
          transition: border-color 0.2s;
          width: 100%;
          white-space: pre-wrap;
        }
        .vuo-fillin-editor a {
          color: ${theme.linkColor};
        }
        .vuo-fillin-editor code {
          background-color: ${theme.lightGreyLighter};
          border-radius: 4px;
          font-size: 1.4rem;
          padding: 2px 4px;
        }
        .vuo-fillin-editor p {
          margin: 0;
        }
        .vuo-fillin-editor-answer {
          background-color: white;
          border: 1px solid ${theme.lightGreyDark};
          border-radius: 4px;
          box-shadow: none;
          color: white;
          cursor: ${isTranslationRef.current ? 'default' : 'pointer'};
          display: inline-block;
          height: 26px;
          margin: 0 3px;
          vertical-align: 2px;
          width: 80px;
        }
        .vuo-fillin-editor-answer:hover,
        .vuo-fillin-editor-answer-active {
          box-shadow: 0 0 6px 1px rgba(0, 0, 0, 0.4);
        }
        .vuo-fillin-editor-answer-remove::before {
          counter-increment: fillin-answers;
          content: counter(fillin-answers) '.';
          color: ${theme.textColorLight};
        }
        .vuo-fillin-editor-answer-remove {
          color: #555;
          font-size: 1.5rem;
          margin-left: 5px;
          width: 100%;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #adb5bd;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default FillinEditor;
